/**
 * QuestionCard - Main card component for displaying questions
 * Clean, minimal design with always-visible examples
 */

import React from 'react';
import { colors, fonts, spacing, radii, layout, transitions } from '../styles/theme';

interface QuestionCardProps {
    questionText: string;
    exampleText: string;
    cartCount: number;
    isLoading: boolean;
    onYes: () => void;
    onNo: () => void;
    onAddToCart: () => void;
    onBack?: () => void;
    onRestart?: () => void;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
    questionText,
    exampleText,
    cartCount,
    isLoading,
    onYes,
    onNo,
    onAddToCart,
    onBack,
    onRestart,
}) => {

    return (
        <div style={styles.card}>
            {/* Cart count indicator */}
            {cartCount > 0 && (
                <div style={styles.cartIndicator}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                        <line x1="3" y1="6" x2="21" y2="6" />
                        <path d="M16 10a4 4 0 0 1-8 0" />
                    </svg>
                    <span>{cartCount} {cartCount === 1 ? 'item' : 'items'} in bag</span>
                </div>
            )}

            {/* Question text - centered */}
            <h2 style={styles.questionText}>
                {questionText}
            </h2>

            {/* Answer buttons row */}
            <div style={styles.buttonRow}>
                <button 
                    style={{ ...styles.button, ...styles.yesButton }}
                    onClick={onYes}
                    disabled={isLoading}
                    aria-label="Yes, I have good control over this"
                >
                    Yes, I have good control over this
                </button>
                <button 
                    style={{ ...styles.button, ...styles.cartButton }}
                    onClick={onAddToCart}
                    disabled={isLoading}
                    aria-label="Add to bag – I would like support"
                >
                    <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor"
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        style={{ marginRight: spacing[2] }}
                    >
                        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                        <line x1="3" y1="6" x2="21" y2="6" />
                        <path d="M16 10a4 4 0 0 1-8 0" />
                    </svg>
                    Add to bag – I would like support
                </button>
                <button 
                    style={{ ...styles.button, ...styles.noButton }}
                    onClick={onNo}
                    disabled={isLoading}
                    aria-label="No, this is not relevant"
                >
                    No, this is not relevant
                </button>
            </div>

            {/* Example: always visible */}
            {exampleText && (
                <div style={styles.exampleWrapper}>
                    <div style={styles.exampleBlock}>
                        <div style={styles.exampleLabel}>Example</div>
                        <p style={styles.exampleContent}>{exampleText}</p>
                    </div>
                </div>
            )}

            {/* Bottom action row: Back (left) + Restart (right) */}
            {(onBack || onRestart) && (
                <div style={styles.restartRow}>
                    {onBack && (
                        <button
                            type="button"
                            style={styles.backButton}
                            onClick={onBack}
                            disabled={isLoading}
                            aria-label="Go back to previous question"
                        >
                            ← Back
                        </button>
                    )}
                    {onRestart && (
                        <button
                            type="button"
                            style={styles.restartButton}
                            onClick={onRestart}
                            aria-label="Restart assessment"
                        >
                            Restart
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    card: {
        width: '100%',
        maxWidth: layout.contentWidth,
        backgroundColor: colors.white,
        border: `1px solid ${colors.border}`,
        borderRadius: radii.lg,
        padding: `${spacing[4]} ${spacing[8]} ${spacing[8]} ${spacing[8]}`,
        margin: '0 auto',
    },
    cartIndicator: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: spacing[2],
        marginBottom: spacing[3],
        fontSize: fonts.sm,
        color: colors.textMuted,
    },
    questionText: {
        textAlign: 'center',
        fontSize: fonts.xl,
        fontWeight: fonts.semibold,
        color: colors.text,
        lineHeight: fonts.lineHeightNormal,
        marginBottom: spacing[8],
        margin: `0 0 ${spacing[8]} 0`,
    },
    buttonRow: {
        display: 'flex',
        justifyContent: 'center',
        gap: spacing[3],
        marginBottom: spacing[8],
        flexWrap: 'wrap',
    },
    button: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[3]} ${spacing[6]}`,
        fontSize: fonts.base,
        fontWeight: fonts.medium,
        border: 'none',
        borderRadius: radii.md,
        cursor: 'pointer',
        transition: `all ${transitions.fast}`,
        minWidth: '100px',
        minHeight: layout.minTouchTarget,
    },
    yesButton: {
        backgroundColor: colors.yes,
        color: colors.white,
    },
    noButton: {
        backgroundColor: colors.no,
        color: colors.white,
    },
    cartButton: {
        backgroundColor: colors.cart,
        color: colors.white,
    },
    exampleWrapper: {
        marginTop: spacing[4],
        border: `1px solid ${colors.border}`,
        borderRadius: radii.lg,
        overflow: 'hidden' as const,
    },
    exampleBlock: {
        backgroundColor: colors.bgLight,
        padding: spacing[4],
    },
    exampleLabel: {
        fontSize: fonts.xs,
        fontWeight: fonts.semibold,
        color: colors.textMuted,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: spacing[2],
    },
    exampleContent: {
        fontSize: fonts.sm,
        color: colors.textSecondary,
        lineHeight: fonts.lineHeightRelaxed,
        margin: 0,
    },
    restartRow: {
        marginTop: spacing[8],
        paddingTop: spacing[6],
        borderTop: `1px solid ${colors.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    backButton: {
        padding: `${spacing[2]} ${spacing[4]}`,
        fontSize: fonts.sm,
        fontWeight: fonts.medium,
        color: colors.textMuted,
        backgroundColor: 'transparent',
        border: `1px solid ${colors.border}`,
        borderRadius: radii.md,
        cursor: 'pointer',
        transition: `all ${transitions.fast}`,
    },
    restartButton: {
        padding: `${spacing[2]} ${spacing[4]}`,
        fontSize: fonts.sm,
        fontWeight: fonts.medium,
        color: colors.textMuted,
        backgroundColor: 'transparent',
        border: `1px solid ${colors.border}`,
        borderRadius: radii.md,
        cursor: 'pointer',
        transition: `all ${transitions.fast}`,
        marginLeft: 'auto',
    },
};

export default QuestionCard;
